import React, { useEffect, useState, useCallback, useRef } from 'react';
import { complaintsApi, servicesApi, inventoryApi, dashboardApi } from '../api/client';
import { Spin } from '../components/UI';
import { TrendUp, TrendDown, AlertTri, Activity, Clock, Package, RefreshCw } from '../components/Icons';
import { useAuth } from '../context/AuthContext';

function StatCard({ label, value, sub, trend, trendUp, color }) {
  return (
    <div className="stat-card">
      <div className="stat-lbl">{label}</div>
      <div className="stat-val" style={color ? { color } : {}}>{value}</div>
      <div className="stat-sub">
        {trend ? (
          <span className={`trend ${trendUp ? 'trend-up' : 'trend-dn'}`}>
            {trendUp ? <TrendUp s={12}/> : <TrendDown s={12}/>}{trend}
          </span>
        ) : sub}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [data, setData]       = useState({ complaints:[], services:[], lowStock:[] });
  const [today, setToday]     = useState(null);
  const [busy, setBusy]       = useState(true);
  const [refreshing, setRef]  = useState(false);
  const [lastUpdated, setLU]  = useState(null);
  const timerRef              = useRef(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setBusy(true); else setRef(true);
    try {
      const base = [
        complaintsApi.list({ page_size: 5000 }),
        servicesApi.list({ page_size: 5000 }),
        inventoryApi.list({ low_stock: true }),
      ];
      const calls = isAdmin ? [...base, dashboardApi.today()] : base;
      const [c, s, i, d] = await Promise.all(calls);
      setData({ complaints: c.data || [], services: s.data || [], lowStock: i.data || [] });
      if (d) setToday(d.data);
      setLU(new Date());
    } catch { }
    finally { if (initial) setBusy(false); else setRef(false); }
  }, [isAdmin]);

  useEffect(() => {
    load(true);
    // auto-refresh every 60s
    timerRef.current = setInterval(() => load(false), 60000);
    // also refresh when the tab regains focus
    const onFocus = () => load(false);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  if (busy) return <Spin label="Loading dashboard…"/>;

  const open     = data.complaints.filter(c => c.status === 'open').length;
  const inProg   = data.complaints.filter(c => c.status === 'in_progress').length;
  const resolved = data.complaints.filter(c => c.status === 'resolved').length;
  const pending  = data.services.filter(s => s.status === 'pending').length;
  const completed= data.services.filter(s => s.status === 'completed').length;

  const fmt = n => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  const fmtTime = d => d ? d.toLocaleTimeString('en-IN', { timeStyle: 'short', hour12: true }) : '';

  return (
    <div>
      {/* Refresh bar */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:10, marginBottom:12 }}>
        {lastUpdated && (
          <span style={{ fontSize:11, color:'var(--text3)' }}>
            Updated {fmtTime(lastUpdated)}
          </span>
        )}
        <button
          onClick={() => load(false)}
          disabled={refreshing}
          className="btn btn-sm"
          style={{ display:'flex', alignItems:'center', gap:5 }}
        >
          <RefreshCw s={12} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}/>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Today's financial summary — admin/superadmin only */}
      {isAdmin && today && (
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)', marginBottom: 0 }}>
          <div className="stat-card" style={{ borderTop: '3px solid var(--green)' }}>
            <div className="stat-lbl">Today's Revenue</div>
            <div className="stat-val" style={{ color: 'var(--green)' }}>{fmt(today.today_revenue)}</div>
            <div className="stat-sub" style={{ display:'flex', flexDirection:'column', gap:2 }}>
              {today.today_sales_count > 0 && <span>{today.today_sales_count} sale{today.today_sales_count !== 1 ? 's' : ''} · {fmt(today.today_sales_revenue)}</span>}
              {today.today_complaint_income > 0 && <span>{today.today_complaint_count} complaint{today.today_complaint_count !== 1 ? 's' : ''} · {fmt(today.today_complaint_income)}</span>}
              {today.today_sales_count === 0 && today.today_complaint_income === 0 && <span>No income yet today</span>}
            </div>
          </div>
          <div className="stat-card" style={{ borderTop: '3px solid var(--red)' }}>
            <div className="stat-lbl">Today's Expenses</div>
            <div className="stat-val" style={{ color: 'var(--red)' }}>{fmt(today.today_expenses)}</div>
            <div className="stat-sub">{today.today_expenses_count} expense{today.today_expenses_count !== 1 ? 's' : ''} logged</div>
          </div>
          <div className="stat-card" style={{ borderTop: '3px solid var(--blue)' }}>
            <div className="stat-lbl">Net Today</div>
            <div className="stat-val" style={{ color: (today.today_revenue - today.today_expenses) >= 0 ? 'var(--green)' : 'var(--red)' }}>
              {fmt(today.today_revenue - today.today_expenses)}
            </div>
            <div className="stat-sub">Revenue minus expenses</div>
          </div>
          <div className="stat-card" style={{ borderTop: '3px solid var(--amber)' }}>
            <div className="stat-lbl">Today's Date</div>
            <div className="stat-val" style={{ fontSize: 16 }}>{new Date().toLocaleDateString('en-IN', { dateStyle: 'medium' })}</div>
            <div className="stat-sub">Live snapshot</div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="stat-grid">
        <StatCard label="Open Complaints"   value={open}             sub="Need attention"      color={open > 0 ? 'var(--red)' : undefined}/>
        <StatCard label="In Progress"        value={inProg}           sub="Being worked on"     color={inProg > 0 ? 'var(--amber)' : undefined}/>
        <StatCard label="Resolved"           value={resolved}         sub="All time"            color="var(--green)"/>
        <StatCard label="Pending Services"   value={pending}          sub="Awaiting dispatch"   color={pending > 0 ? 'var(--amber)' : undefined}/>
        <StatCard label="Services Completed" value={completed}        sub="All time"            color="var(--green)"/>
        <StatCard label="Low Stock Items"    value={data.lowStock.length} sub="Below threshold" color={data.lowStock.length > 0 ? 'var(--red)' : undefined}/>
        <StatCard label="Total Complaints"   value={data.complaints.length} sub="All time"/>
        <StatCard label="Total Services"     value={data.services.length}   sub="All time"/>
      </div>

      {/* Main content + alerts */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 320px', gap:16 }}>

        {/* Recent complaints */}
        <div className="card">
          <div className="card-hd">
            <div className="card-title"><Activity s={15}/>Recent Complaints</div>
          </div>
          <div className="card-body" style={{ padding:0 }}>
            {data.complaints.length === 0
              ? <div style={{ padding:24, color:'var(--text2)', fontSize:13 }}>No complaints raised yet.</div>
              : data.complaints.slice(0, 6).map(c => (
                <div key={c.id} className="divider-row" style={{ padding:'10px 20px' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{c.complaint_code}</div>
                    <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>{c.complaint_type}</div>
                  </div>
                  <span className={`badge ${c.status==='open'?'b-err':c.status==='in_progress'?'b-warn':'b-ok'}`}>
                    {c.status.replace('_',' ')}
                  </span>
                </div>
              ))
            }
          </div>
        </div>

        {/* Pending services */}
        <div className="card">
          <div className="card-hd">
            <div className="card-title"><Clock s={15}/>Pending Services</div>
          </div>
          <div className="card-body" style={{ padding:0 }}>
            {data.services.filter(s=>s.status==='pending').length === 0
              ? <div style={{ padding:24, color:'var(--text2)', fontSize:13 }}>No pending services.</div>
              : data.services.filter(s=>s.status==='pending').slice(0,6).map(s => (
                <div key={s.id} className="divider-row" style={{ padding:'10px 20px' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600 }}>{s.service_code}</div>
                    <div style={{ fontSize:11, color:'var(--text2)', marginTop:2 }}>{s.service_type}</div>
                  </div>
                  <div style={{ fontSize:11, color:'var(--text3)' }}>{s.scheduled_date || 'No date'}</div>
                </div>
              ))
            }
          </div>
        </div>

        {/* Alerts panel */}
        <div>
          <div className="card">
            <div className="card-hd"><div className="card-title" style={{ color:'var(--blue)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.08em' }}>Alerts</div></div>
            <div className="card-body">
              {data.lowStock.length > 0 && data.lowStock.map(p => (
                <div key={p.id} className="alert-row">
                  <AlertTri s={14}/>
                  <span>Low stock — {p.name} ({p.stock_qty} left)</span>
                </div>
              ))}
              {open > 0 && (
                <div className="alert-row"><AlertTri s={14}/><span>{open} complaint{open>1?'s':''} still open</span></div>
              )}
              {pending > 0 && (
                <div className="alert-row"><AlertTri s={14}/><span>{pending} service request{pending>1?'s':''} pending</span></div>
              )}
              {data.lowStock.length===0 && open===0 && pending===0 && (
                <div style={{ fontSize:12, color:'var(--text3)' }}>No alerts at this time.</div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-hd"><div className="card-title"><Package s={15}/>Low Stock</div></div>
            <div className="card-body" style={{ padding:0 }}>
              {data.lowStock.length === 0
                ? <div style={{ padding:'12px 20px', color:'var(--text2)', fontSize:12 }}>All products stocked.</div>
                : data.lowStock.map(p => (
                  <div key={p.id} className="divider-row" style={{ padding:'8px 20px' }}>
                    <span style={{ fontSize:12, flex:1 }}>{p.name}</span>
                    <span className={`badge ${p.stock_qty <= 3 ? 'b-err' : 'b-warn'}`}>{p.stock_qty} left</span>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
